<script setup lang="ts">
import WreathIcon from '~/components/design-system/icons/WreathIcon.vue';
import useLaunchGame from '~/composables/useLaunchGame';
import type { IGameCategory } from '~/interfaces/dto/games/response';
import { trackingMethods } from '~/plugins/analytics.client';
const { $t, $enabled } = useNuxtApp();
const gameStore = useGameStore();
const authStore = useAuthStore();
const siteStore = useSiteStore();
const { $winnerService } = useServices();

const imagePath = useImagePath;
const formatCurrency = useFormatCurrency;
const launchGameComp = useLaunchGame;
const router = useRouter();

const { getPlaceholder } = useBranding();

const winners = computed(
  () =>
    gameStore.returnWinnersCircle?.bigWinners ||
    gameStore.returnWinnersCircle?.hotGames,
);

const label = computed(() =>
  !!gameStore.returnWinnersCircle?.bigWinners?.length
    ? $t('big-winners')
    : $t('hot-games'),
);
const featureFlag = computed(
  () =>
    siteStore.getFeatureFlags.get('website.enablerecentwinnersmarquee') ||
    false,
);

const isMarqueeEnabled = computed(
  () => featureFlag.value && winners.value?.length,
);

const gameUnavailable = computed(() => {
  return (game: IGameCategory) => {
    if (game?.unavailable) return true;
    return gameStore.returnDisabledProviders.includes(game?.provider);
  };
});

function launchGame(game: IGameCategory) {
  if (gameUnavailable.value(game)) return;
  launchGameComp({
    game,
    component: 'big-winners-marquee',
  });
  // send GA tracking method when game is launched
  trackingMethods.launchReal(
    game.name,
    authStore?.currentUser?.username,
    'winners-marquee.vue',
  );
}

function navigate() {
  trackingMethods.goToWinners();
  router.push('/winners');
}

onMounted(async () => {
  await $winnerService.fetchAllWinners();
});
</script>

<template>
  <div
    v-if="isMarqueeEnabled"
    class="w-full bg-layer-2 dark:bg-layer-1 text-base-priority rounded-lg flex overflow-hidden mt-2"
  >
    <div
      :class="{
        'flex p-2 px-3 items-center shadow-8 font-bold fit-content': true,
        'cursor-pointer': $enabled('website.enablewinnerspage'),
      }"
      @click="$enabled('website.enablewinnerspage') ? navigate() : null"
    >
      <WreathIcon class="hidden md:block w-6 h-6" />
      <div class="ml-1 text-sm md:text-normal">
        {{ label }}
      </div>
    </div>
    <div
      v-if="!!winners"
      class="marquee marquee--hover-pause enable-animation py-2"
    >
      <div
        v-for="n in 2"
        :key="n"
        class="marquee__content flex"
        :aria-hidden="n === 2"
      >
        <div
          v-for="(winner, index) in winners"
          :key="`${n}-${index}`"
          class="flex items-center mr-3 cursor-pointer glow p-2 rounded-lg"
          @click="launchGame(winner?.game)"
        >
          <DesignSystemLazyImage
            :src="imagePath(winner?.game?.tileImage)"
            :placeholder="getPlaceholder('categoryCardPlaceholder')"
            :alt="winner?.gameName"
            class="h-9 w-9 fit-cover border-round overflow-hidden"
          />
          <div class="text-xs ml-2">
            <div class="mb-1">{{ winner?.gameName }}</div>
            <div class="font-bold">
              {{ winner?.mobileNumber }} |
              <span
                class="text-primary-gold-600 font-bold"
                v-html="formatCurrency(winner?.payoutAmount)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fit-content {
  min-width: fit-content;
  @media screen and (max-width: 768px) {
    min-width: unset;
  }
}
/* Marquee styles */
.marquee {
  --gap: 1rem;
  position: relative;
  display: flex;
  overflow: hidden;
  user-select: none;
  gap: var(--gap);
}

.marquee__content {
  flex-shrink: 0;
  display: flex;
  justify-content: space-around;
  gap: var(--gap);
  min-width: 100%;
}

@keyframes scroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-100% - var(--gap)));
  }
}

/* Pause animation when reduced-motion is set */
@media (prefers-reduced-motion: reduce) {
  .marquee__content {
    animation-play-state: paused !important;
  }
}

/* Enable animation */
.enable-animation .marquee__content {
  animation: scroll 100s linear infinite;
  @media screen and (max-width: 768px) {
    animation: scroll 120s linear infinite;
  }
}

/* Pause on hover */
.marquee--hover-pause:hover .marquee__content {
  animation-play-state: paused;
}

/* A fit-content sizing fix: Absolute position the duplicate container. This will set the size of the parent wrapper to a single child container. Shout out to Olavi's article that had this solution 👏 @link: https://olavihaapala.fi/2021/02/23/modern-marquee.html  */
.marquee--pos-absolute .marquee__content:last-child {
  position: absolute;
  top: 0;
  left: 0;
}

/* Enable position absolute animation on the duplicate content (last-child) */
.enable-animation .marquee--pos-absolute .marquee__content:last-child {
  animation-name: scroll-abs;
}

@keyframes scroll-abs {
  from {
    transform: translateX(calc(100% + var(--gap)));
  }
  to {
    transform: translateX(0);
  }
}
</style>
